/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { sumBy } from 'lodash'

import type { AccessorFn } from '@elastic/charts'

import type { ChartItemType } from '@modules/billing-api/customTypes'
import { isCCMFeatureActivated } from '@modules/ccm-user-lib/feature'

import { ProductType } from '../../../types'
import { mapProductToProductType } from '../../../BillingUsageOverviewV2/types'

import type { Solution, ChartItem } from '../../../types'
import type { ReducerState } from '../../Filters/filtersReducer'

export const prepareAggregatedProductDataAccessors = (filters: ReducerState) => {
  const aggregatedValues: ChartItemType[] = []

  if (filters.productTypes.includes(ProductType.Deployments)) {
    aggregatedValues.push('deployment')
  }

  if (isCCMFeatureActivated() && filters.productTypes.includes(ProductType.CCM)) {
    aggregatedValues.push('cloud_connect')
  }

  if (filters.productTypes.includes(ProductType.Projects)) {
    aggregatedValues.push(...filters.solutions)
  }

  const accessors: AccessorFn[] = []
  aggregatedValues.map((aggregatedValue) => {
    const accessor: AccessorFn = ({ values }) => {
      const items = values.filter(({ type }) => type === aggregatedValue)
      accessor.fieldName = aggregatedValue
      return sumBy(items, 'value') || 0
    }

    accessors.push(accessor)
  })

  return accessors
}

export const hasAnyDataToDisplay = ({
  data,
  filters,
}: {
  data: ChartItem[]
  filters: ReducerState
}): boolean =>
  data.some(({ values }) => {
    const filteredData = values
      // Filter data by product type
      .filter((product) => filters.productTypes.includes(mapProductToProductType[product.type]))
      // Filter data by a solution
      .filter((product) => {
        // Deployments and CCM are already filtered by product type, so include them
        if (product.type === 'deployment' || product.type === 'cloud_connect') {
          return true
        }

        return filters.solutions.includes(product.type as Solution)
      })

    if (filteredData.length > 0) {
      return true
    }

    return false
  })
